/* DirectorDemoMode — Floating widget voor sales-demonstraties.
 *
 * - "Start Director Demo" knop start een sessie via /directie/demo-sessions/start.
 * - Toont een checklist (7 taken) + timer per taak + globale klik-teller.
 * - Klikpaden worden anoniem geregistreerd (target = data-testid).
 * - Aan het eind: feedback-dialog (1-5 sterren + kudos/blockers) → summary.
 *
 * Alleen zichtbaar voor management-rollen. Overlay z-index [400] boven overige
 * dialogs. State bewaard in sessionStorage zodat een refresh de sessie hervat.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Play, StopCircle, CheckCircle2, SkipForward, Sparkles, X,
  ChevronDown, ChevronUp, MousePointerClick, Clock, Star,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SESSION_KEY = "webster:demo-session";
const MANAGEMENT = new Set(["superadmin", "platform_admin", "locatiemanager", "directeur", "financieel"]);

const fmtMs = (ms) => {
  if (!ms) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

export default function DirectorDemoMode() {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showFinish, setShowFinish] = useState(false);
  const [rating, setRating] = useState(0);
  const [kudos, setKudos] = useState("");
  const [blockers, setBlockers] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const activeTaskRef = useRef(null);

  // Session-restore bij page refresh — houdt demo-sessie levend.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) setSession(JSON.parse(raw));
    } catch {}
  }, []);
  useEffect(() => {
    try {
      if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      else sessionStorage.removeItem(SESSION_KEY);
    } catch {}
  }, [session]);

  const isMgmt = user && MANAGEMENT.has(user.role);
  // Verberg de widget in normale schermen. Wordt pas actief bij expliciete
  // opt-in via `?demo=1` in de URL, of wanneer een sessie al loopt.
  const optedIn = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("demo") === "1";

  const logEvent = useCallback(async (body) => {
    if (!session?.id) return;
    try { await api.post(`/directie/demo-sessions/${session.id}/event`, body); }
    catch { /* silent — analytics mag never de UI blokkeren */ }
  }, [session]);

  // Klikpad-registratie: capture data-testid van geklikt element (of ancestor).
  useEffect(() => {
    if (!session || summary) return;
    const handler = (e) => {
      // Skip clicks binnen de widget zelf om noise te voorkomen.
      const inWidget = e.target.closest?.("[data-testid='director-demo-widget']");
      if (inWidget) return;
      const el = e.target.closest?.("[data-testid]");
      const target = el?.getAttribute?.("data-testid");
      setClickCount((c) => c + 1);
      logEvent({
        type: "click", target: target || null,
        path: window.location.pathname,
        task_key: activeTaskRef.current || null,
      });
    };
    window.addEventListener("click", handler, true);
    return () => window.removeEventListener("click", handler, true);
  }, [session, summary, logEvent]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/directie/demo-sessions/start");
      setSession(r.data);
      setClickCount(0);
      setSummary(null);
      toast.success("Director-demo gestart — klikpaden worden opgenomen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon demo niet starten");
    } finally { setBusy(false); }
  };

  const startTask = (key) => {
    activeTaskRef.current = key;
    setSession((s) => s && {
      ...s,
      tasks: s.tasks.map((t) => t.key === key && !t.started_at
        ? { ...t, started_at: new Date().toISOString() } : t),
    });
    logEvent({ type: "task_start", task_key: key });
  };
  const completeTask = (key) => {
    activeTaskRef.current = null;
    setSession((s) => s && {
      ...s,
      tasks: s.tasks.map((t) => t.key === key
        ? { ...t, completed_at: new Date().toISOString(),
            duration_ms: t.started_at
              ? (new Date() - new Date(t.started_at))
              : null }
        : t),
    });
    logEvent({ type: "task_complete", task_key: key });
    toast.success("Taak afgerond");
  };
  const skipTask = (key) => {
    activeTaskRef.current = null;
    setSession((s) => s && {
      ...s,
      tasks: s.tasks.map((t) => t.key === key
        ? { ...t, skipped: true, completed_at: new Date().toISOString() } : t),
    });
    logEvent({ type: "task_skip", task_key: key });
  };

  const finish = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/directie/demo-sessions/${session.id}/finish`, {
        rating: rating || null,
        kudos: kudos.trim() || null,
        blockers: blockers.trim() || null,
      });
      setSummary(r.data);
      setSession(null);
      setShowFinish(false);
      setRating(0); setKudos(""); setBlockers("");
      toast.success("Demo afgesloten — bedankt voor je feedback");
    } catch (e) {
      toast.error("Feedback opslaan mislukt");
    } finally { setBusy(false); }
  };

  if (!isMgmt) return null;
  // Toon alleen als expliciet opt-in via ?demo=1 OF een lopende sessie hersteld is.
  if (!optedIn && !session && !summary) return null;

  // ── Summary panel (na finish) ────────────────────────────────────────────
  if (summary) {
    const c = summary.computed || {};
    return (
      <div className="fixed bottom-4 right-4 z-[400] max-w-sm w-[92vw] sm:w-96"
           data-testid="director-demo-widget">
        <Card className="border-2 border-emerald-300 bg-emerald-50 shadow-2xl p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              <h3 className="font-semibold text-slate-900">Demo afgerond</h3>
            </div>
            <button onClick={() => setSummary(null)} className="text-slate-400 hover:text-slate-700"
                    data-testid="director-demo-close-summary">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><div className="text-slate-500 uppercase text-[10px]">Duur</div>
              <div className="text-lg font-bold text-slate-900">{fmtMs(c.total_duration_ms)}</div></div>
            <div><div className="text-slate-500 uppercase text-[10px]">Taken voltooid</div>
              <div className="text-lg font-bold text-slate-900">{c.tasks_completed}/{c.tasks_total}</div></div>
            <div><div className="text-slate-500 uppercase text-[10px]">Kliks</div>
              <div className="text-lg font-bold text-slate-900">{c.click_count}</div></div>
            <div><div className="text-slate-500 uppercase text-[10px]">Beoordeling</div>
              <div className="text-lg font-bold text-slate-900">
                {summary.feedback?.rating ? `${summary.feedback.rating}/5` : "—"}
              </div></div>
          </div>
        </Card>
      </div>
    );
  }

  // ── Inactive: alleen 'Start Director Demo' knop ──────────────────────────
  if (!session) {
    return (
      <div className="fixed bottom-4 right-4 z-[400]" data-testid="director-demo-widget">
        <Button onClick={start} disabled={busy}
                className="bg-fuchsia-700 hover:bg-fuchsia-800 shadow-lg"
                data-testid="start-director-demo-btn">
          <Sparkles className="h-4 w-4 mr-1.5" /> Start Director Demo
        </Button>
      </div>
    );
  }

  // ── Active session: floating panel met taken + timer ─────────────────────
  const done = session.tasks.filter((t) => t.completed_at).length;

  return (
    <div className="fixed bottom-4 right-4 z-[400] w-[92vw] sm:w-96 max-w-md"
         data-testid="director-demo-widget">
      <Card className="border-2 border-fuchsia-300 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-fuchsia-700 to-pink-600 text-white">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-semibold">Director-demo actief</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] flex items-center gap-1" data-testid="director-demo-clicks">
              <MousePointerClick className="h-3 w-3" /> {clickCount}
            </span>
            <button onClick={() => setCollapsed((c) => !c)} className="hover:opacity-80"
                    data-testid="director-demo-toggle">
              {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {!collapsed && (
          <>
            <div className="p-3 text-xs text-slate-600 border-b border-slate-100 flex items-center justify-between">
              <span>Taken: <strong>{done}/{session.tasks.length}</strong></span>
              <span className="text-slate-400">Klikpaden opgenomen</span>
            </div>
            <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100"
                data-testid="director-demo-task-list">
              {session.tasks.map((t) => {
                const isActive = activeTaskRef.current === t.key;
                const isDone = !!t.completed_at;
                return (
                  <li key={t.key} className={`p-2.5 flex items-center gap-2 text-sm ${
                    isActive ? "bg-fuchsia-50" : isDone ? "bg-slate-50/50" : ""
                  }`} data-testid={`director-demo-task-${t.key}`}>
                    {isDone ? (
                      <CheckCircle2 className={`h-4 w-4 shrink-0 ${t.skipped ? "text-slate-400" : "text-emerald-600"}`} />
                    ) : (
                      <Clock className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                    <span className={`flex-1 truncate ${isDone && !t.skipped ? "line-through text-slate-500" : ""}`}>
                      {t.label}
                    </span>
                    {t.duration_ms != null && (
                      <span className="text-[10px] text-slate-500 tabular-nums">
                        {fmtMs(t.duration_ms)}
                      </span>
                    )}
                    {!isDone && !isActive && (
                      <Button size="sm" variant="ghost" onClick={() => startTask(t.key)}
                              className="h-6 px-2 text-[11px]"
                              data-testid={`director-demo-task-start-${t.key}`}>
                        <Play className="h-3 w-3" />
                      </Button>
                    )}
                    {isActive && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => completeTask(t.key)}
                                className="h-6 px-2 text-[11px] text-emerald-700"
                                data-testid={`director-demo-task-complete-${t.key}`}>
                          <CheckCircle2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => skipTask(t.key)}
                                className="h-6 px-2 text-[11px] text-slate-500"
                                data-testid={`director-demo-task-skip-${t.key}`}>
                          <SkipForward className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="p-3 border-t border-slate-100 flex justify-end">
              <Button size="sm" onClick={() => setShowFinish(true)}
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="director-demo-finish-btn">
                <StopCircle className="h-3.5 w-3.5 mr-1" /> Beëindig & feedback
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* Feedback dialog */}
      {showFinish && (
        <div className="fixed inset-0 z-[450] flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setShowFinish(false); }}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-white rounded-lg shadow-2xl max-w-md w-full p-5"
               data-testid="director-demo-feedback-dialog">
            <h3 className="font-semibold text-slate-900 mb-1">Hoe was de demo?</h3>
            <p className="text-xs text-slate-500 mb-3">Je feedback helpt ons volgende sales-demo's te verbeteren.</p>
            <div className="flex gap-1 mb-3" data-testid="director-demo-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)}
                        className="p-1 rounded hover:bg-amber-50"
                        data-testid={`director-demo-star-${n}`}>
                  <Star className={`h-6 w-6 ${n <= rating ? "fill-amber-400 text-amber-500" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <label className="text-xs text-slate-500 block">Wat viel op (positief)</label>
            <textarea value={kudos} onChange={(e) => setKudos(e.target.value)}
                      rows={2} placeholder="Bijv. 'directeur was verrast door €18k/mnd potentie'"
                      className="w-full border rounded p-2 text-sm mb-2"
                      data-testid="director-demo-kudos" />
            <label className="text-xs text-slate-500 block">Blockers / verbeterpunten</label>
            <textarea value={blockers} onChange={(e) => setBlockers(e.target.value)}
                      rows={2} placeholder="Bijv. 'wilde BSN-import screenshot'"
                      className="w-full border rounded p-2 text-sm mb-3"
                      data-testid="director-demo-blockers" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowFinish(false)}
                      data-testid="director-demo-cancel-finish">
                Annuleren
              </Button>
              <Button onClick={finish} disabled={busy}
                      className="bg-fuchsia-700 hover:bg-fuchsia-800"
                      data-testid="director-demo-submit-feedback">
                {busy ? "Opslaan…" : "Verstuur & sluit"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
